import { useState, useCallback } from 'react';
import { useFieldArray, Control, UseFormSetValue } from 'react-hook-form';

interface UsePersonListProps {
    control: Control<any>;
    name: string;
    setValue: UseFormSetValue<any>;
    onPersonSaved?: (person: any) => void;
}

export const usePersonList = ({ control, name, setValue, onPersonSaved }: UsePersonListProps) => {
    const { fields, append, remove, prepend, replace } = useFieldArray({
        control,
        name,
    });

    // Modal state
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [tempPerson, setTempPerson] = useState<any>(null);
    const [manualAddressMode, setManualAddressMode] = useState(false);

    const openAdd = useCallback(() => {
        setTempPerson({
            fullName: '',
            relationship: '',
            companyName: '',
            email: '',
            address: { line1: '', line2: '', city: '', postcode: '' },
            isOver18: true,
            isNew: true
        });
        setEditingIndex(fields.length);
        setManualAddressMode(false);
    }, [fields.length]);

    const openEdit = useCallback((index: number, currentData: any) => {
        setTempPerson({
            ...currentData,
            address: currentData.address || { line1: '', line2: '', city: '', postcode: '' },
            isNew: false
        });
        setEditingIndex(index);
        setManualAddressMode(false);
    }, []);

    const close = useCallback(() => {
        setEditingIndex(null);
        setTempPerson(null);
    }, []);

    const save = useCallback((data: any) => {
        if (editingIndex !== null && tempPerson) {
            const personData = { ...tempPerson, ...data };
            const { isNew, ...saveData } = personData;

            if (isNew) {
                append(saveData);
            } else {
                // We can't use `update` from useFieldArray because it's not available in all versions or behaves differently
                // So we use setValue to update specific fields
                // Actually, for deep nested updates, setValue is safer
                const prefix = `${name}.${editingIndex}`;
                setValue(`${prefix}.fullName`, saveData.fullName);
                setValue(`${prefix}.relationship`, saveData.relationship);
                setValue(`${prefix}.companyName`, saveData.companyName);
                setValue(`${prefix}.email`, saveData.email);
                setValue(`${prefix}.address`, saveData.address);
                setValue(`${prefix}.isOver18`, saveData.isOver18);
            }

            if (onPersonSaved) {
                onPersonSaved(saveData);
            }
        }
        close();
    }, [editingIndex, tempPerson, append, setValue, name, onPersonSaved, close]);

    return {
        fields,
        append,
        remove,
        replace,
        modal: {
            isOpen: editingIndex !== null && tempPerson !== null,
            openAdd,
            openEdit,
            close,
            save,
            data: tempPerson,
            setData: setTempPerson,
            isEditing: editingIndex !== null && !tempPerson?.isNew,
            editingIndex
        },
        manualAddressMode,
        setManualAddressMode
    };
};
